import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, TextInput,
} from 'react-native';
import { useLocalSearchParams, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import ControllerEquipmentPanel from '../../../src/components/controller/ControllerEquipmentPanel';
import { api, apiPost, apiDelete } from '../../../src/api/client';
import { useRouter } from 'expo-router';
import { useTheme } from '../../../src/context/ThemeContext';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import InlineConfirm from '../../../src/components/InlineConfirm';
import InlineMessage from '../../../src/components/InlineMessage';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import { rs, fs } from '../../../src/utils/responsive';
import PageFooter from '../../../src/components/PageFooter';

interface ControllerDetail {
  id: string;
  name?: string | null;
  firmwareVersion: string | null;
  isOnline: boolean;
  wifiRssi: number | null;
  ipAddress: string | null;
  uptime: number | null;
  freeHeap: number | null;
  lastSeen: string | null;
  relayStates: number[] | null;
  relayCycles: number[] | null;
  /** 1.4 P04: hardware model from the catalog drives the relay grid size. */
  model?: string;
  modelLabel?: string;
  relayCount?: number;
  inputCount?: number;
  reportedModel?: string | null;
  bayId: string | null;
  bayName: string | null;
  locationName: string | null;
  otaStatus: string | null;
  otaVersion: string | null;
  createdAt: string;
}

interface LogEntry {
  id: string;
  direction: string;
  topic: string;
  payload: any;
  createdAt: string;
}

/** ControllerDetail - telemetry, relay states, firmware, and MQTT logs for a controller. */
export default function ControllerDetail() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const router = useRouter();
  const styles = useStyles(colors);
  const toast = useToast();
  const [ctrl, setCtrl] = useState<ControllerDetail | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  // Round 2: owners name boards from this page (the name shows in every picker and relay map).
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name) { toast.show({ type: 'error', title: 'Enter a name' }); return; }
    setSavingName(true);
    try { await api(`/api/controllers/${id}`, { method: 'PUT', body: { name } }); toast.show({ type: 'success', title: 'Controller renamed' }); fetchData(); }
    catch (err: any) { toast.show({ type: 'error', title: 'Could not rename', message: err.message }); }
    finally { setSavingName(false); }
  };
  const [cmdLoading, setCmdLoading] = useState('');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<'restart' | 'remove' | null>(null);

  const fetchData = async (signal?: AbortSignal) => {
    try {
      const [ctrlData, logsData] = await Promise.all([
        api<ControllerDetail>(`/api/controllers/${id}`),
        api<{ logs: LogEntry[] }>(`/api/controllers/${id}/logs?limit=20`),
      ]);
      if (signal?.aborted) return;
      setCtrl(ctrlData);
      setLogs(logsData.logs || []);
    } catch (err: any) {
      if (!(signal?.aborted)) console.error(err);
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  };

  useEffect(() => { const ac = new AbortController(); fetchData(ac.signal); return () => ac.abort(); }, [id]);

  const sendCommand = async (command: string, extra?: any) => {
    setCmdLoading(command);
    try {
      const result = await apiPost<{ success: boolean; error?: string }>(`/api/controllers/${id}/command`, { command, ...extra });
      const msg = result.success ? `${command} executed successfully` : `${command} failed: ${result.error}`;
      setInlineMsg({ type: result.success ? 'success' : 'error', message: msg });
      fetchData();
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message || 'Command failed' });
    } finally {
      setCmdLoading('');
    }
  };

  const confirmRestart = () => {
    setConfirmAction('restart');
  };

  const handleRemoveController = () => {
    setConfirmAction('remove');
  };

  const doRemoveController = async () => {
    setConfirmAction(null);
    try {
      await apiDelete(`/api/controllers/${id}`);
      setInlineMsg({ type: 'success', message: 'Controller removed from your account' });
      router.back();
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message || 'Failed to remove controller' });
    }
  };

  if (loading) {
    return (
      <ContentContainer role="owner">
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: rs(80) }} />
      </ContentContainer>
    );
  }

  if (!ctrl) {
    return (
      <ContentContainer role="owner">
        <View style={styles.centered}>
          <Text style={styles.errorText}>Controller not found</Text>
        </View>
      </ContentContainer>
    );
  }

  const relayCount = ctrl.relayCount || 4;
  const relayIdx = Array.from({ length: relayCount }, (_, i) => i);
  const relayStates = relayIdx.map(i => ctrl.relayStates?.[i] ?? 0);
  const relayCycles = relayIdx.map(i => ctrl.relayCycles?.[i] ?? 0);

  return (
    <ContentContainer role="owner" footer={false}>
      <Stack.Screen options={{ title: ctrl.id }} />
      <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: rs(40) }}>
        {/* Status Header */}
        <View style={styles.statusCard}>
          <View style={styles.statusHeader}>
            <View style={[styles.statusDot, { backgroundColor: ctrl.isOnline ? '#059669' : '#dc2626' }]} />
            <Text style={styles.statusText}>
              {ctrl.isOnline ? 'Online' : 'Offline'}
            </Text>
            {ctrl.lastSeen && (
              <Text style={styles.lastSeen}>
                Last seen: {new Date(ctrl.lastSeen).toLocaleString()}
              </Text>
            )}
          </View>
        </View>

        {/* Round 2: name */}
        <Text style={styles.sectionTitle}>Name</Text>
        <View style={[styles.card, { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }]}>
          <TextInput value={nameDraft} onChangeText={setNameDraft} placeholder={ctrl.name || 'e.g. Bay 2 board'} placeholderTextColor={colors.textLight} maxLength={32}
            style={{ flex: 1, minWidth: 160, borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8, color: colors.text, backgroundColor: colors.surface }} />
          <TouchableOpacity onPress={saveName} disabled={savingName} style={{ paddingHorizontal: spacing.md, paddingVertical: 8, borderRadius: borderRadius.sm, backgroundColor: colors.primary, opacity: savingName ? 0.6 : 1 }}>
            <Text style={{ color: '#fff', fontWeight: fontWeight.semibold }}>{savingName ? 'Saving…' : 'Save name'}</Text>
          </TouchableOpacity>
        </View>

        {/* Telemetry */}
        <Text style={styles.sectionTitle}>Telemetry</Text>
        <View style={styles.card}>
          <InfoRow label="Device ID" value={ctrl.id} colors={colors} mono copyable />
          <InfoRow label="Model" value={`${ctrl.modelLabel || ctrl.model || 'RCP-Alpha-PoE'} · ${relayCount} relays${(ctrl.inputCount ?? 0) > 0 ? ` · ${ctrl.inputCount} inputs` : ''}`} colors={colors} />
          {!!ctrl.reportedModel && (
            <InfoRow label="Reported model" value={`${ctrl.reportedModel} (does not match — contact support)`} colors={colors} />
          )}
          <InfoRow label="Firmware" value={ctrl.firmwareVersion || 'Unknown'} colors={colors} />
          <InfoRow label="IP Address" value={ctrl.ipAddress || 'N/A'} colors={colors} />
          <InfoRow label="WiFi RSSI" value={ctrl.wifiRssi != null ? `${ctrl.wifiRssi} dBm` : 'N/A'} colors={colors} />
          <InfoRow label="Uptime" value={ctrl.uptime != null ? formatUptime(ctrl.uptime) : 'N/A'} colors={colors} />
          <InfoRow label="Free Heap" value={ctrl.freeHeap != null ? `${Math.round(ctrl.freeHeap / 1024)} KB` : 'N/A'} colors={colors} />
        </View>

        {/* 1.4 P19: relays with action labels, inputs, buzzer, events */}
        <ControllerEquipmentPanel ctrl={ctrl as any} client={{ get: (p) => api(p), put: (p, b) => api(p, { method: 'PUT', body: b }) }} onLive={(s) => setCtrl(c => (c && c.id === s.id && (c.isOnline !== s.isOnline || c.lastSeen !== s.lastSeen)) ? { ...c, isOnline: s.isOnline, lastSeen: s.lastSeen } : c)} onSettingsChanged={() => fetchData()} notify={(type, message) => setInlineMsg({ type, message })} />

        {/* Assignment */}
        {(ctrl.bayName || ctrl.locationName) && (
          <>
            <Text style={styles.sectionTitle}>Assignment</Text>
            <View style={styles.card}>
              {ctrl.locationName && <InfoRow label="Location" value={ctrl.locationName} colors={colors} />}
              {ctrl.bayName && <InfoRow label="Bay" value={ctrl.bayName} colors={colors} />}
            </View>
          </>
        )}

        {/* Commands */}
        <Text style={styles.sectionTitle}>Commands</Text>
        <View style={styles.commandGrid}>
          <CommandBtn
            icon="flash" label="Test Relays" color="#059669"
            loading={cmdLoading === 'relay_test'}
            onPress={() => sendCommand('relay_test')}
          />
          <CommandBtn
            icon="pulse" label="Request Status" color={colors.primary}
            loading={cmdLoading === 'status'}
            onPress={() => sendCommand('status')}
          />
          <CommandBtn
            icon="refresh" label="Restart" color="#dc2626"
            loading={cmdLoading === 'restart'}
            onPress={confirmRestart}
          />
        </View>
        <InlineConfirm
          message="Are you sure you want to restart this controller?"
          visible={confirmAction === 'restart'}
          onConfirm={() => { setConfirmAction(null); sendCommand('restart'); }}
          onCancel={() => setConfirmAction(null)}
          confirmLabel="Restart"
          variant="danger"
          loading={cmdLoading === 'restart'}
        />

        {/* Test Single Relay */}
        <Text style={styles.sectionTitle}>Test Single Relay</Text>
        <View style={styles.relayTestGrid}>
          {relayIdx.map(i => i + 1).map(relay => (
            <TouchableOpacity
              key={relay}
              style={styles.relayTestBtn}
              onPress={() => sendCommand('test_relay', { relay, mode: 'pulse' })}
              disabled={cmdLoading === `test_relay_${relay}`}
            >
              {cmdLoading === `test_relay_${relay}` ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <>
                  <Ionicons name="flash-outline" size={18} color={colors.primary} />
                  <Text style={styles.relayTestLabel}>Relay {relay}</Text>
                </>
              )}
            </TouchableOpacity>
          ))}
        </View>
        <InlineMessage
          type={inlineMsg?.type || 'success'}
          message={inlineMsg?.message || ''}
          visible={!!inlineMsg}
          onDismiss={() => setInlineMsg(null)}
          autoDismissMs={4000}
        />

        {/* Recent Logs */}
        <Text style={styles.sectionTitle}>Recent MQTT Activity</Text>
        {logs.length === 0 ? (
          <Text style={styles.noLogs}>No recent activity</Text>
        ) : (
          <View style={styles.logsCard}>
            {logs.map((log) => {
              const isHeartbeat = log.topic?.includes('/status/heartbeat');
              // Strip device ID from topic for display: rp/{deviceId}/status/x -> rp/.../status/x
              const displayTopic = log.topic?.replace(/^rp\/[^/]+\//, 'rp/.../') || log.topic;
              return (
              <TouchableOpacity
                key={log.id}
                activeOpacity={0.7}
                onPress={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
              >
                <View style={styles.logRow}>
                  <View style={styles.logLeft}>
                    <Ionicons
                      name={isHeartbeat ? 'heart' : (log.direction === 'inbound' ? 'arrow-down' : 'arrow-up')}
                      size={14}
                      color={isHeartbeat ? '#ec4899' : (log.direction === 'inbound' ? '#059669' : '#3b82f6')}
                    />
                    <Text style={styles.logTopic} numberOfLines={expandedLogId === log.id ? undefined : 1}>{displayTopic}</Text>
                  </View>
                  <Text style={styles.logTime}>
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </Text>
                </View>
                {expandedLogId === log.id && log.payload && (
                  <View style={styles.logPayload}>
                    <Text style={styles.logPayloadText}>
                      {typeof log.payload === 'string' ? log.payload : JSON.stringify(log.payload, null, 2)}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            );
            })}
          </View>
        )}

        {/* Remove from account */}
        <View style={{ marginTop: spacing.xl, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: colors.borderLight }}>
          <TouchableOpacity style={styles.removeBtn} onPress={handleRemoveController}>
            <Ionicons name="remove-circle-outline" size={18} color="#dc2626" />
            <Text style={styles.removeBtnText}>Remove from My Account</Text>
          </TouchableOpacity>
          <Text style={styles.removeHint}>
            This will unassign the controller from any bays and return it to the unclaimed pool.
          </Text>
          <InlineConfirm
            message="Remove this controller from your account? It will be unassigned from any bays and returned to the unclaimed pool."
            visible={confirmAction === 'remove'}
            onConfirm={doRemoveController}
            onCancel={() => setConfirmAction(null)}
            confirmLabel="Remove"
            variant="danger"
          />
        </View>
        <PageFooter />
      </ScrollView>
    </ContentContainer>
  );
}

function InfoRow({ label, value, colors, mono, copyable }: { label: string; value: string; colors: any; mono?: boolean; copyable?: boolean }) {
  const toast = useToast();
  const [valueWidth, setValueWidth] = useState(0);
  const onCopy = async () => {
    try {
      await Clipboard.setStringAsync(value);
      toast.show({ type: 'success', title: 'Copied' });
    } catch {
      toast.show({ type: 'error', title: 'Copy failed' });
    }
  };
  // For monospace values, scale font down to fit the full string on one line.
  // Monospace glyphs are ~0.62 * fontSize wide; compute the largest fontSize
  // that still fits the measured container width.
  const maxFont = mono ? fs(13) : fs(14);
  const minFont = fs(7);
  const fittedFont = mono && valueWidth > 0 && value.length > 0
    ? Math.max(minFont, Math.min(maxFont, valueWidth / (value.length * 0.62)))
    : maxFont;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: rs(6), gap: spacing.sm }}>
      <Text style={{ fontSize: fs(14), color: colors.textSecondary }}>{label}</Text>
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
        <View
          style={{ flex: 1 }}
          onLayout={(e) => setValueWidth(e.nativeEvent.layout.width)}
        >
          <Text
            style={{ fontSize: fittedFont, color: colors.text, fontWeight: '500', fontFamily: mono ? 'monospace' : undefined, textAlign: 'right' }}
            numberOfLines={1}
          >
            {value}
          </Text>
        </View>
        {copyable && (
          <TouchableOpacity
            onPress={onCopy}
            accessibilityRole="button"
            accessibilityLabel={`Copy ${label}`}
            hitSlop={8}
            style={{ padding: 2 }}
          >
            <Ionicons name="copy-outline" size={16} color={colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

function CommandBtn({ icon, label, color, loading, onPress }: {
  icon: string; label: string; color: string; loading: boolean; onPress: () => void;
}) {
  return (
    <TouchableOpacity
      style={{
        flex: 1, alignItems: 'center', paddingVertical: rs(14), borderRadius: 10,
        backgroundColor: color + '15', borderWidth: 1, borderColor: color + '30',
      }}
      onPress={onPress}
      disabled={loading}
    >
      {loading ? (
        <ActivityIndicator size="small" color={color} />
      ) : (
        <>
          <Ionicons name={icon as keyof typeof Ionicons.glyphMap} size={22} color={color} />
          <Text style={{ fontSize: fs(12), color, fontWeight: '600', marginTop: 4 }}>{label}</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorText: { fontSize: fontSize.md, color: c.textSecondary },
  statusCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.borderLight,
  },
  statusHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  statusDot: { width: 12, height: 12, borderRadius: 6 },
  statusText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  lastSeen: { fontSize: fontSize.xs, color: c.textSecondary, marginLeft: 'auto' },
  sectionTitle: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary,
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: spacing.sm, marginTop: spacing.md,
  },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    borderWidth: 1, borderColor: c.borderLight,
  },
  relayGrid: { flexDirection: 'row', gap: spacing.sm },
  relayCard: {
    flex: 1, alignItems: 'center', paddingVertical: spacing.md, borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  relayOff: { backgroundColor: c.surface, borderColor: c.borderLight },
  relayOn: { backgroundColor: '#dc262615', borderColor: '#dc2626' },
  relayLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.textSecondary },
  relayLabelOn: { color: '#dc2626' },
  relayState: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  relayStateOn: { color: '#dc2626', fontWeight: fontWeight.bold },
  relayCycles: { fontSize: fs(10), color: c.textLight, marginTop: 2 },
  commandGrid: { flexDirection: 'row', gap: spacing.sm },
  relayTestGrid: { flexDirection: 'row', gap: spacing.sm },
  relayTestBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 4, paddingVertical: rs(10), borderRadius: borderRadius.sm,
    backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight,
  },
  relayTestLabel: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium },
  logsCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight,
    overflow: 'hidden',
  },
  logRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: rs(8), paddingHorizontal: spacing.md,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  logLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
  logTopic: { fontSize: fontSize.xs, color: c.text, fontFamily: 'monospace', flex: 1 },
  logTime: { fontSize: fontSize.xs, color: c.textLight },
  noLogs: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', paddingVertical: spacing.lg },
  logPayload: {
    backgroundColor: c.background, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  logPayloadText: {
    fontSize: fs(11), fontFamily: 'monospace', color: c.text, lineHeight: fs(16),
  },
  removeBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    paddingVertical: spacing.md, borderRadius: borderRadius.md,
    backgroundColor: '#dc262610', borderWidth: 1, borderColor: '#dc262625',
  },
  removeBtnText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: '#dc2626' },
  removeHint: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center', marginTop: spacing.xs },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
